import React from 'react';

import { User } from '../../../ai.h2o.cloud.appstore';
import { useQueryMyWorkspaces, useQueryWorkspace } from '../../../authz/authz.hooks';
import { userName } from '../../../authz/gen/ai/h2o/user/v1/user_pb';
import { Workspace } from '../../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { GrpcStatusCode, isGrpcGatewayErrorResponse } from '../../../services/HTTPResponse';

const getWorkspaceIdFromUrl = (pathname: string) => {
  // TODO: Make it work for all apps supporting workspaces.
  return pathname.includes('/orchestrator/workspaces/') && !pathname.endsWith('workspaces/')
    ? pathname.replace('/orchestrator/', '').split('/', 2)[1]
    : pathname.includes('/mlops/workspaces/') && !pathname.endsWith('workspaces/')
    ? pathname.replace('/mlops/', '').split('/', 2)[1]
    : undefined;
};

export interface WorkspaceQueriesResult {
  activeWorkspace?: Workspace;
  activeWorkspaceValid?: boolean;
  activeWorkspaceInitialized: boolean;
  initializationError?: string;
  defaultWorkspace?: Workspace;
  workspaceFromURL?: Workspace;
  workspaceFromURLFetched: boolean;
  workspaceNotFound: boolean;
  workspaceIdFromURL?: string;
}

interface UseWorkspaceQueriesParams {
  user: User;
  activeWorkspaceId?: string;
  pathname: string;
}

/**
 * Hook that manages all workspace-related queries
 * Handles active workspace validation, default workspace fetching, and URL workspace resolution
 */
export const useWorkspaceQueries = (params: UseWorkspaceQueriesParams): WorkspaceQueriesResult => {
  const { user, activeWorkspaceId, pathname } = params;
  // Validate that the active workspace stored in localStorage is valid for the current session
  const activeWorkspaceQuery = useQueryWorkspace(user.isPublic ? undefined : activeWorkspaceId, {
    retry(failureCount, error) {
      const isNotFoundOrUnauthenticatedError = Boolean(
        isGrpcGatewayErrorResponse(error) &&
          (error.code === GrpcStatusCode.NOT_FOUND || error.code === GrpcStatusCode.PERMISSION_DENIED)
      );
      // skip retrying if the workspace is not found or user is not authenticated for the workspace, otherwise retry up to 3 times
      return !isNotFoundOrUnauthenticatedError && failureCount <= 3;
    },
    staleTime: 1_000, // 1 second - the workspace is fetched in the `activateWorkspace` function, we can count on that result when validating after workspace switch
  });

  const activeWorkspaceValid =
    !activeWorkspaceQuery || activeWorkspaceQuery.isLoading ? undefined : activeWorkspaceQuery.isSuccess; // if the value is undefined, the state is unknown yet

  // Fetch user's personal (default) workspace
  const defaultWorkspaceQuery = useQueryMyWorkspaces(
    {
      filter: `personal_workspace_subject='${userName.compile({ user: user.id })}'`,
    },
    {
      enabled: !user.isPublic,
    }
  );
  const defaultWorkspace = defaultWorkspaceQuery.data?.pages[0]?.workspaces?.[0];

  // Fetch workspace from URL if present
  const workspaceIdFromURL = React.useMemo(() => getWorkspaceIdFromUrl(pathname), [pathname]);
  const workspaceFromURLQuery = useQueryWorkspace(workspaceIdFromURL, {
    enabled: !user.isPublic,
    retry(failureCount, error) {
      const isNotFoundError = error && typeof error === 'object' && (error as any)?.code === GrpcStatusCode.NOT_FOUND;
      // skip retrying if the workspace is not found, otherwise retry up to 3 times
      return !isNotFoundError && failureCount <= 3;
    },
  });
  const workspaceFromURL = workspaceFromURLQuery?.data;
  // technically there might be different reasons for error, but we render the NotFound for all of them
  const workspaceNotFound = Boolean(workspaceFromURLQuery?.isError);

  // Track initialization state - once validated, it stays true
  const workspaceInitializedRef = React.useRef(false);
  if (activeWorkspaceValid === true) {
    // the ref sets only to `true`, therefore once the active workspace is validated, the value is locked on `true` as initialized and never changes
    workspaceInitializedRef.current = true;
  }
  const activeWorkspaceInitialized = workspaceInitializedRef.current;

  // Track initialization errors - computed after all queries are defined
  const initializationError = React.useMemo(() => {
    // If active workspace validation explicitly failed
    if (activeWorkspaceValid === false && activeWorkspaceQuery?.error) {
      return `Failed to validate active workspace: ${
        isGrpcGatewayErrorResponse(activeWorkspaceQuery.error)
          ? `Error code ${activeWorkspaceQuery.error.code}`
          : 'Unknown error'
      }`;
    }

    // If list workspaces query failed (used to fetch default workspace)
    if (!user.isPublic && defaultWorkspaceQuery.isError && defaultWorkspaceQuery.error) {
      return `Failed to load workspaces: ${
        isGrpcGatewayErrorResponse(defaultWorkspaceQuery.error)
          ? `Error code ${defaultWorkspaceQuery.error.code}. ${defaultWorkspaceQuery.error.message}`
          : 'Unknown error'
      }`;
    }

    return undefined;
  }, [
    activeWorkspaceValid,
    activeWorkspaceQuery?.error,
    defaultWorkspaceQuery.isError,
    defaultWorkspaceQuery.error,
    user.isPublic,
  ]);

  return {
    activeWorkspace: activeWorkspaceQuery?.data,
    activeWorkspaceValid,
    activeWorkspaceInitialized,
    initializationError,
    defaultWorkspace,
    workspaceFromURL,
    workspaceFromURLFetched: workspaceFromURLQuery?.isFetched ?? false,
    workspaceNotFound,
    workspaceIdFromURL,
  };
};
